"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { flipText } from "@/lib/tool-kit/text/upside-down";

/** English strings. A translated content file overrides these through `ui`. */
export const UPSIDE_DOWN_TEXT_GENERATOR_STRINGS = {
  inputText: "Input Text",
  inputPlaceholder: "Type or paste your text here...",
  mirrorLabel:
    "Also mirror backwards (keep letter order — reads backwards when rotated)",
  outputLabel: "Upside down output",
  copied: "Copied!",
  copy: "Copy",
  outputPlaceholder: "Type above to see output…",
} as const;

export default function UpsideDownTextGeneratorWidget() {
  const strings = useToolUi(UPSIDE_DOWN_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [mirror, setMirror] = useState(false);
  const [copied, setCopied] = useState(false);

  const output = flipText(input, mirror);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="flip-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="flip-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Options */}
      <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
        <input
          checked={mirror}
          className="accent-primary"
          onChange={(e) => setMirror(e.target.checked)}
          type="checkbox"
        />
        {strings.mirrorLabel}
      </label>

      {/* Output */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-foreground text-sm">
            {strings.outputLabel}
          </p>
          <button
            className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
            disabled={output.length === 0}
            onClick={handleCopy}
            type="button"
          >
            {copied ? strings.copied : strings.copy}
          </button>
        </div>
        <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
          {output.length > 0 ? (
            output
          ) : (
            <span className="text-muted-foreground">
              {strings.outputPlaceholder}
            </span>
          )}
        </p>
      </div>
    </>
  );
}
